/**
 * Dev-sync J3a — Download: the chart + sidebar table composited into one PNG.
 *
 * Port of legacy exportChartAsPng + _sidebarTableSize + _drawSidebarTable +
 * _downloadDataUrl (psychrometric_chart.js:874-1031). The chart half comes
 * from Plotly's own toImage (its SVG rendering is authoritative); the
 * sidebar is drawn directly with the Canvas 2D text API from the same
 * sidebar_paths the HTML table renders from — legacy deliberately did NOT
 * rasterize the DOM because its vendored html2canvas (0.4.x) predates
 * Flexbox and garbled the sidebar (js:874-885); the hand-drawn table is the
 * mechanism, so it is what gets ported.
 *
 * ★ Preserved legacy inconsistency: the exported label separator for a
 * UDA-hosted row is `tag + " : " + kind` (SPACE-colon-SPACE, js:993) while
 * the HTML sidebar uses `tag + ": " + kind` (js:1247). Ported verbatim.
 *
 * Filename: psychrometric_chart.png (js:1026). Failures log to the console
 * exactly like legacy (no user-facing status).
 */

import type { PsyChartData, PsySeason } from './psychrometricApi';
import type { PlotlyChartNode, PlotlyStatic } from './usePlotly';
import { KIND_LABELS, fmtRowT, sidebarVisibleRows } from './SidebarTable';

interface SidebarSizing {
  width: number;
  height: number;
  colWidths: number[];
  headerHeight: number;
  rowHeight: number;
  groupGap: number;
}

/** Pure layout math, no drawing — sizes the combined canvas before anything
 * is drawn. Constants must match what _drawSidebarTable draws with
 * (js:920-945). */
function sidebarTableSize(data: PsyChartData | null, season: PsySeason): SidebarSizing {
  const colWidths = [170, 55, 55, 55, 55];
  const headerHeight = 22;
  const rowHeight = 18;
  const groupGap = 14;
  const paths = (data && data.sidebar_paths) || [];
  let totalHeight = 0;
  paths.forEach((path) => {
    const visibleRows = sidebarVisibleRows(path, season);
    if (!visibleRows.length) return;
    totalHeight += headerHeight + rowHeight + visibleRows.length * rowHeight + groupGap;
  });
  return {
    width: colWidths.reduce((a, b) => a + b, 0),
    height: totalHeight,
    colWidths,
    headerHeight,
    rowHeight,
    groupGap,
  };
}

/** js:947-1022 — the sidebar table in Canvas 2D, matching whichever season
 * tab (summer/winter) is currently selected so the exported image matches
 * what's on screen (sidebarVisibleRows shared with the HTML table). */
function drawSidebarTable(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  season: PsySeason,
  sizing: SidebarSizing,
  data: PsyChartData | null
): void {
  const paths = (data && data.sidebar_paths) || [];
  const { colWidths, rowHeight } = sizing;
  const totalWidth = sizing.width;
  let curY = y;
  ctx.textBaseline = 'middle';

  paths.forEach((path) => {
    const visibleRows = sidebarVisibleRows(path, season);
    if (!visibleRows.length) return;

    ctx.font = 'bold 12px Arial, sans-serif';
    ctx.fillStyle = '#212121';
    ctx.textAlign = 'left';
    ctx.fillText(path.name, x, curY + sizing.headerHeight / 2);
    curY += sizing.headerHeight;

    ctx.font = 'bold 11px Arial, sans-serif';
    ctx.fillStyle = '#616161';
    const headers = ['EADB', 'EAWB', 'LADB', 'LAWB'];
    let colX = x + colWidths[0];
    headers.forEach((label, i) => {
      ctx.textAlign = 'right';
      ctx.fillText(label, colX + colWidths[i + 1] - 4, curY + rowHeight / 2);
      colX += colWidths[i + 1];
    });
    curY += rowHeight;
    ctx.strokeStyle = '#e0e0e0';
    ctx.beginPath();
    ctx.moveTo(x, curY);
    ctx.lineTo(x + totalWidth, curY);
    ctx.stroke();

    visibleRows.forEach((row, idx) => {
      const kindLabel = row.component_long || KIND_LABELS[row.component_kind ?? ''] || 'Plenum';
      // Tag replaces the kind name entirely, except UDA-hosted rows whose
      // tag is inherited from the host Plenum (js:982-993) — note the
      // " : " separator, see the header doc.
      const isUdaHosted =
        row.component_kind === 'electric_heater' || row.component_kind === 'humidifier';
      const label = row.tag ? (isUdaHosted ? `${row.tag} : ${kindLabel}` : row.tag) : kindLabel;
      const cond = row[season];
      const ent = (cond && cond.entering) || { t_db: null, t_wb: null };
      const leav = (cond && cond.leaving) || { t_db: null, t_wb: null };
      const isStale = !!(cond && cond.stale);

      if (idx % 2 === 1) {
        ctx.fillStyle = '#fafafa';
        ctx.fillRect(x, curY, totalWidth, rowHeight);
      }

      ctx.font = '11px Arial, sans-serif';
      ctx.fillStyle = isStale ? '#c62828' : '#212121';
      ctx.textAlign = 'left';
      const truncated = label.length > 26 ? `${label.slice(0, 25)}…` : label;
      ctx.fillText(truncated, x + 4, curY + rowHeight / 2);

      const vals = [fmtRowT(ent.t_db), fmtRowT(ent.t_wb), fmtRowT(leav.t_db), fmtRowT(leav.t_wb)];
      let vx = x + colWidths[0];
      ctx.textAlign = 'right';
      vals.forEach((v, i) => {
        // The LEAVING pair (i 2/3) is never red, same as the HTML table —
        // staleness is about the ENTERING conditions (requested 2026-10-16,
        // see ahuBoardOverrides.scss). This sheet has its own palette, so
        // the plain colour here is #212121, not the table's #555.
        ctx.fillStyle = isStale && i < 2 ? '#c62828' : '#212121';
        ctx.fillText(v, vx + colWidths[i + 1] - 4, curY + rowHeight / 2);
        vx += colWidths[i + 1];
      });
      curY += rowHeight;
    });

    curY += sizing.groupGap;
  });
}

/** js:1024-1031. */
function downloadDataUrl(dataUrl: string): void {
  const link = document.createElement('a');
  link.download = 'psychrometric_chart.png';
  link.href = dataUrl;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/** js:886-918 — Plotly.toImage for the chart at its on-screen size, then the
 * hand-drawn sidebar table beside it on one white canvas. */
export function exportChartAsPng(
  plotly: PlotlyStatic,
  chartNode: PlotlyChartNode | null,
  data: PsyChartData | null,
  sidebarSeason: PsySeason
): void {
  if (!chartNode) return;
  plotly
    .toImage(chartNode, {
      format: 'png',
      width: chartNode.offsetWidth,
      height: chartNode.offsetHeight,
    })
    .then((chartDataUrl) => {
      const chartImg = new Image();
      chartImg.onload = () => {
        const gap = 24;
        const padding = 12;
        const season = sidebarSeason || 'summer';
        const sizing = sidebarTableSize(data, season);
        const combined = document.createElement('canvas');
        combined.width = chartImg.width + gap + padding * 2 + sizing.width;
        combined.height = Math.max(chartImg.height, sizing.height + padding * 2);
        const ctx = combined.getContext('2d');
        if (!ctx) return;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, combined.width, combined.height);
        ctx.drawImage(chartImg, 0, 0);
        drawSidebarTable(ctx, chartImg.width + gap + padding, padding, season, sizing, data);
        downloadDataUrl(combined.toDataURL('image/png'));
      };
      chartImg.onerror = (err) => {
        // eslint-disable-next-line no-console
        console.error('Psychrometric chart PNG export failed to load chart image:', err);
      };
      chartImg.src = chartDataUrl;
    })
    .catch((err) => {
      // eslint-disable-next-line no-console
      console.error('Psychrometric chart PNG export failed:', err);
    });
}
